import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import IconButton from "../../../DesignSystem/Buttons/IconButton";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/paraplanningDashboard/Cross.svg";
import { parseOpeningBalanceCsv } from "../utils/openingBalanceBulkUpload";
import { uploadOpeningBalanceRows } from "../services/openingBalanceBulkUploadService";
import {
  saveIORecipientMapping,
  subscribeIORecipientMappings,
} from "../services/ioRecipientMappingService";
import { getIOIntroducers } from "../../FCIStatements/fciStatementsService";
import { ioIntroducerTargets } from "../utils/ioRecipientTargets";
import { useUserEmail } from "../../../SignIn/UserEmailContext";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";

const money = (value) =>
  value === null
    ? "-"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(Number(value));

const displayDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

const unknownOptionId = (row) =>
  row?.mappingTargetType === "unknown" && row.mappingTargetId
    ? row.mappingTargetId
    : `unknown:${row?.rowNumber || "row"}`;

const OpeningBalanceBulkUpload = ({ advisers, loadingAdvisers, monthKey }) => {
  const { userEmail } = useUserEmail();
  const {
    advisers: advisersWithUnappointed,
    loadingAdvisers: loadingAdvisersWithUnappointed,
  } = useAdviserListWithUnappointed();
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mappingRow, setMappingRow] = useState(null);
  const [mappingPosition, setMappingPosition] = useState(null);
  const [savedMappings, setSavedMappings] = useState({});
  const [mappingsLoading, setMappingsLoading] = useState(true);
  const [introducers, setIntroducers] = useState([]);
  const [introducersLoading, setIntroducersLoading] = useState(true);

  const availableAdvisers = useMemo(() => {
    const byEmail = new Map();
    [...advisers, ...advisersWithUnappointed].forEach((adviser) => {
      const email = String(adviser.email || adviser.id || "").trim().toLowerCase();
      if (!email) return;
      byEmail.set(email, { ...byEmail.get(email), ...adviser });
    });
    return [...byEmail.values()];
  }, [advisers, advisersWithUnappointed]);

  const mappingOptions = useMemo(
    () => [
      ...availableAdvisers
        .map((adviser) => ({
          id: `adviser:${String(adviser.email || adviser.id || "").trim().toLowerCase()}`,
          name: `[Adviser${adviser.isUnappointed ? " · Unappointed" : ""}] ${adviser.name || adviser.email} — ${adviser.email || adviser.id}`,
          target: {
            type: "adviser",
            targetId: String(adviser.email || adviser.id || "").trim().toLowerCase(),
            email: String(adviser.email || adviser.id || "").trim().toLowerCase(),
            name: adviser.name || adviser.email || adviser.id,
            adviser,
          },
        }))
        .filter((option) => option.target.targetId),
      ...ioIntroducerTargets(introducers).map((introducer) => ({
        id: introducer.id,
        name: introducer.optionName,
        target: introducer,
      })),
    ].sort((left, right) => left.name.localeCompare(right.name, "en-GB")),
    [availableAdvisers, introducers],
  );

  useEffect(() => subscribeIORecipientMappings(
    (mappings) => {
      setSavedMappings(mappings);
      setMappingsLoading(false);
    },
    () => {
      setSavedMappings({});
      setMappingsLoading(false);
    },
  ), []);

  useEffect(() => {
    let active = true;
    getIOIntroducers(userEmail)
      .then((rows) => {
        if (active) setIntroducers(rows);
      })
      .catch(() => {
        if (active) setIntroducers([]);
      })
      .finally(() => {
        if (active) setIntroducersLoading(false);
      });
    return () => { active = false; };
  }, [userEmail]);

  const applyTarget = (row, target, status = "Manually mapped") => {
    if (target.type === "unknown") return applyUnknownTarget(row);
    if (target.type === "adviser") {
      const selected = target.adviser || {};
      return {
        ...row,
        mappingTargetType: "adviser",
        mappingTargetId: target.targetId,
        mappingTargetName: target.name,
        mappingDisplay: `[Adviser] ${target.name}`,
        adviserEmail: selected.email || target.email || target.targetId,
        adviserName: selected.name || target.name || "",
        adviserId: selected.adviserId || "",
        brandName: selected.brandName || "",
        mappingStatus: status,
      };
    }
    return {
      ...row,
      mappingTargetType: "introducer",
      mappingTargetId: target.targetId,
      mappingTargetName: target.name,
      mappingDisplay: `[IO Introducer] ${target.name}`,
      adviserEmail: "",
      adviserName: "",
      adviserId: "",
      brandName: target.name,
      mappingStatus: status,
    };
  };

  const applyUnknownTarget = (row) => ({
    ...row,
    mappingTargetType: "unknown",
    mappingTargetId: `unknown:${String(row.sourceName || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")}`,
    mappingTargetName: row.sourceName,
    mappingDisplay: `[Unknown] ${row.sourceName}`,
    adviserEmail: "",
    adviserName: "",
    adviserId: "",
    brandName: row.sourceName,
    mappingStatus: "Unknown",
  });

  const unmatchedCount = rows.filter((row) => !row.mappingTargetId).length;
  const entryCount = rows.reduce(
    (count, row) =>
      count + Number(row.openingBalance !== null) + Number(row.advance !== null),
    0,
  );

  const selectFile = async (nextFile) => {
    setFile(nextFile || null);
    setRows([]);
    setMessage("");
    setError("");
    if (!nextFile) return;
    try {
      const parsedRows = parseOpeningBalanceCsv(
        await nextFile.text(),
        availableAdvisers,
        monthKey,
      );
      const nextRows = parsedRows.map((row) => {
        if (row.adviserEmail) {
          const option = mappingOptions.find(
            (item) => item.id === `adviser:${String(row.adviserEmail).toLowerCase()}`,
          );
          return option ? applyTarget(row, option.target, row.mappingStatus) : row;
        }
        const saved = Object.values(savedMappings).find(
          (mapping) => String(mapping.sourceKey || "").toLowerCase() === String(row.sourceName || "").trim().toLowerCase(),
        );
        if (!saved) return applyUnknownTarget(row);
        const targetType = String(saved.targetType || saved.entityType || "").toLowerCase();
        const targetId = String(saved.targetId || saved.assignedAdviserEmail || saved.ioIntroducerId || "").trim().toLowerCase();
        const option = mappingOptions.find(
          (item) => item.id.toLowerCase() === `${targetType}:${targetId}`,
        );
        return option
          ? applyTarget(row, option.target, "Saved mapping")
          : applyUnknownTarget(row);
      });
      if (!nextRows.length) {
        throw new Error("No Opening Balance or Advance entries were found.");
      }
      setRows(nextRows);
    } catch (parseError) {
      setError(parseError.message || "The CSV could not be read.");
    }
  };

  const openMapping = (event, row) => {
    event.stopPropagation();
    const anchor = event.currentTarget.getBoundingClientRect();
    const popupWidth = 380;
    setMappingPosition({
      left: Math.max(
        12,
        Math.min(anchor.left - popupWidth - 8, window.innerWidth - popupWidth - 12),
      ),
      top: Math.max(12, Math.min(anchor.top, window.innerHeight - 360)),
    });
    setMappingRow(row);
  };

  const closeMapping = () => {
    setMappingRow(null);
    setMappingPosition(null);
  };

  const mapRow = async (rowNumber, optionId) => {
    const option = [
      ...mappingOptions,
      mappingRow
        ? {
            id: unknownOptionId(mappingRow),
            target: {
              type: "unknown",
              targetId: `unknown:${mappingRow.rowNumber}`,
              name: mappingRow.sourceName,
            },
          }
        : null,
    ].filter(Boolean).find((item) => item.id === optionId);
    if (!option?.target || !mappingRow) return;
    if (option.target.type === "unknown") {
      setRows((current) => current.map((row) =>
        row.rowNumber === rowNumber ? applyUnknownTarget(row) : row,
      ));
      closeMapping();
      return;
    }
    try {
      await saveIORecipientMapping({
        sourceName: mappingRow.sourceName,
        target: option.target,
      });
      setRows((current) => current.map((row) =>
        row.rowNumber === rowNumber ? applyTarget(row, option.target) : row,
      ));
      closeMapping();
    } catch (mappingSaveError) {
      setError(mappingSaveError.message || "The recipient mapping could not be saved.");
    }
  };

  const upload = async () => {
    setError("");
    setMessage("");
    setUploading(true);
    try {
      const uploaded = await uploadOpeningBalanceRows({
        rows,
        monthKey,
        fileName: file?.name,
      });
      setMessage(`${uploaded} entries uploaded successfully.`);
    } catch (uploadError) {
      setError(uploadError.message || "The entries could not be uploaded.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="website-dashboard-card opening-balance-upload column gap-m">
      <div className="column gap-s">
        <h4 className="FP_H4">September opening balances and advances</h4>
        <p className="FP_Content">
          Upload the CSV to review adviser and IO introducer mappings before anything is saved.
          Commission-related columns are ignored.
        </p>
      </div>
      <FileUpload
        label="Upload CSV"
        accept=".csv,text/csv"
        fileName={file?.name}
        onFilesChange={selectFile}
        disabled={loadingAdvisers || loadingAdvisersWithUnappointed || mappingsLoading || introducersLoading || uploading}
        status={error ? "error" : rows.length ? "success" : "default"}
        statusMessage={
          error ||
          (rows.length
            ? `${file.name}: ${rows.length} rows and ${entryCount} entries ready to review.`
            : loadingAdvisers || loadingAdvisersWithUnappointed
              ? "Loading advisers before matching..."
              : "")
        }
      />
      {rows.length ? (
        <>
          <div className="row space-between align-center gap-m opening-balance-upload-summary">
            <p className="FP_Content">
              <strong>{rows.length}</strong> rows · <strong>{entryCount}</strong> entries ·{" "}
              <strong>{unmatchedCount}</strong> require mapping
            </p>
            <PrimaryButton
              type="button"
              label={uploading ? "UPLOADING..." : "UPLOAD REVIEWED ENTRIES"}
              disabled={uploading || unmatchedCount > 0}
              onClick={upload}
            />
          </div>
          {message ? <p className="FP_Content text-success">{message}</p> : null}
          <div className="website-table-wrap table-scroll-container opening-balance-upload-table-wrap">
            <table className="website-table opening-balance-upload-table" aria-label="Opening balance bulk upload review">
              <thead>
                <tr>
                  <th className="FP_Help_Text_Semibold uppercase">Row</th>
                  <th className="FP_Help_Text_Semibold uppercase">CSV Name</th>
                  <th className="FP_Help_Text_Semibold uppercase text-align-right">Opening Balance</th>
                  <th className="FP_Help_Text_Semibold uppercase">Opening Date</th>
                  <th className="FP_Help_Text_Semibold uppercase text-align-right">Advance</th>
                  <th className="FP_Help_Text_Semibold uppercase">Advance Date</th>
                  <th className="FP_Help_Text_Semibold uppercase">Mapped To</th>
                  <th className="FP_Help_Text_Semibold uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.rowNumber}
                    className={!row.adviserEmail ? "monthly-payment-unlinked-email-row" : ""}
                  >
                    <td className="FP_Help_Text black">{row.rowNumber}</td>
                    <td className="FP_Help_Text black">{row.sourceName}</td>
                    <td className="FP_Help_Text black text-align-right">{money(row.openingBalance)}</td>
                    <td className="FP_Help_Text black">{row.openingBalance === null ? "-" : displayDate(row.openingBalanceDate)}</td>
                    <td className="FP_Help_Text black text-align-right">{money(row.advance)}</td>
                    <td className="FP_Help_Text black">{row.advance === null ? "-" : displayDate(row.advanceDate)}</td>
                    <td className="FP_Help_Text black">
                      <div className="row align-center gap-s">
                        <IconButton
                          icon={<EditIcon aria-hidden="true" />}
              label={`Change recipient mapping for ${row.sourceName}`}
                          onClick={(event) => openMapping(event, row)}
                        />
                        <span>{row.mappingDisplay || row.adviserEmail || "Select adviser or IO introducer"}</span>
                      </div>
                    </td>
                    <td className={`FP_Help_Text_Semibold ${row.mappingTargetType === "unknown" ? "" : row.mappingTargetId ? "text-success" : "text-error"}`}>
                      {row.mappingStatus}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      {mappingRow && mappingPosition
        ? createPortal(
            <div
              className="opening-balance-mapping-popover"
              style={mappingPosition}
              role="dialog"
              aria-label="Map opening balance row to an adviser or IO introducer"
              onClick={(event) => event.stopPropagation()}
            >
              <IconButton
                className="opening-balance-mapping-close"
                icon={<CloseIcon aria-hidden="true" />}
                label="Close recipient mapping"
                onClick={closeMapping}
              />
              <CustomSelect
                label={`Map ${mappingRow.sourceName} to`}
                options={[
                  {
                    id: unknownOptionId(mappingRow),
                    name: `[Unknown] ${mappingRow.sourceName}`,
                  },
                  ...mappingOptions,
                ]}
                value={mappingRow.mappingTargetType && mappingRow.mappingTargetId
                  ? `${mappingRow.mappingTargetType}:${mappingRow.mappingTargetId}`
                  : ""}
                placeholder="Select adviser or IO introducer"
                searchPlaceholder="Search adviser, email, or introducer"
                required
                clearable={false}
                onChange={(event) => mapRow(mappingRow.rowNumber, event.target.value)}
              />
              <div className="opening-balance-mapping-actions">
                <SecondaryButton label="Cancel" onClick={closeMapping} />
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
};

export default OpeningBalanceBulkUpload;
